import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

const paymentLabels = {
  cash: 'Cash',
  credit: 'Credit',
  online: 'Online',
  upi: 'UPI',
  gpay: 'GPay',
  paytm: 'Paytm',
  phonepe: 'PhonePe',
};

const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;

export default function DealerPanel() {
  const [stats, setStats] = useState({});
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [services, setServices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [activeTab, setActiveTab] = useState('customers');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const downloadBlob = async (url, fileName) => {
    try {
      const res = await axios.get(url, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(err.response?.data?.message || 'Report download failed');
    }
  };
  const buildDateQuery = useCallback((start = '', end = '') => {
    const params = new URLSearchParams();
    if (start && end) {
      params.set('from_date', start);
      params.set('to_date', end);
    }
    return params.toString();
  }, []);
  const downloadDealerLedger = () => {
    const query = buildDateQuery(fromDate, toDate);
    downloadBlob(`${API_URL}/api/payments/ledger/report?type=dealer${query ? `&${query}` : ''}`, 'dealer-credit-debit-ledger-details.xlsx');
  };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/dealer/panel`, { headers: getHeaders() });
      setStats(res.data);
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  const fetchCustomers = useCallback(async (start = '', end = '') => {
    try {
      let url = `${API_URL}/api/users/dealer/customers`;
      if (start && end) url += `?from_date=${start}&to_date=${end}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setCustomers(res.data);
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  const fetchSales = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/sales`, { headers: getHeaders() });
      setSales(res.data);
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/services`, { headers: getHeaders() });
      setServices(res.data || []);
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  const fetchPayments = useCallback(async (start = '', end = '') => {
    try {
      let url = `${API_URL}/api/users/dealer/payments`;
      if (start && end) url += `?from_date=${start}&to_date=${end}`;
      const res = await axios.get(url, { headers: getHeaders() });
      setPayments(res.data);
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  const fetchLedger = useCallback(async (start = '', end = '') => {
    try {
      const query = buildDateQuery(start, end);
      const res = await axios.get(`${API_URL}/api/payments/ledger${query ? `?${query}` : ''}`, { headers: getHeaders() });
      setLedger(res.data);
    } catch (err) { console.log(err); }
  }, [buildDateQuery, getHeaders]);

  useEffect(() => {
    fetchStats();
    fetchCustomers();
    fetchSales();
    fetchServices();
    fetchPayments();
    fetchLedger();
  }, [fetchStats, fetchCustomers, fetchSales, fetchServices, fetchPayments, fetchLedger]);

  const applyFilter = () => {
    fetchCustomers(fromDate, toDate);
    fetchPayments(fromDate, toDate);
    fetchLedger(fromDate, toDate);
  };

  const clearFilter = () => {
    setFromDate('');
    setToDate('');
    fetchCustomers('', '');
    fetchPayments('', '');
    fetchLedger('', '');
  };

  const inp = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' };
  const totalCredit = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const modeTotal = (modes) => payments.reduce((sum, p) => modes.includes(p.payment_mode) ? sum + Number(p.amount || 0) : sum, 0);
  const visibleSales = sales.filter((item) => {
    if (!fromDate || !toDate || !item.created_at) return true;
    const date = new Date(item.created_at).toISOString().slice(0, 10);
    return date >= fromDate && date <= toDate;
  });
  const visibleServices = services.filter((item) => {
    if (!fromDate || !toDate) return true;
    const rawDate = item.start_date || item.expiry_date || item.created_at;
    if (!rawDate) return true;
    const date = new Date(rawDate).toISOString().slice(0, 10);
    return date >= fromDate && date <= toDate;
  });
  const ledgerTotalRow = ledger.find((item) => item.row_type === 'Dealer Total' || item.account_type === 'dealer');
  const finalDebit = ledgerTotalRow
    ? Number(ledgerTotalRow.total_debit ?? ledgerTotalRow.total_purchase ?? 0)
    : visibleSales.reduce((sum, item) => sum + Number(item.total_amount || 0), 0);
  const finalCredit = ledgerTotalRow
    ? Number(ledgerTotalRow.total_credit ?? ledgerTotalRow.total_paid ?? 0)
    : payments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const finalBalance = finalDebit - finalCredit;
  const finalStatus = finalBalance > 0 ? 'Amount payable to dealer' : finalBalance < 0 ? 'Amount receivable from dealer' : 'Settled';

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ color: '#1e3a5f', margin: 0 }}>My Dealer Panel</h2>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>View-only sales, customer, payment and ledger records for your own customers.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Vehicles', value: stats.total_customers || 0, color: '#3b82f6' },
          { label: 'Tagged Devices', value: stats.total_devices || 0, color: '#0ea5e9' },
          { label: 'Tagged Services', value: services.length || 0, color: '#6366f1' },
          { label: 'Paid Customers', value: stats.paid_customers || 0, color: '#10b981' },
          { label: 'Pending Customers', value: stats.pending_customers || 0, color: '#ef4444' },
          { label: 'Renewals Due', value: stats.renewals_due || 0, color: '#f59e0b' },
          { label: 'Total Sales', value: stats.total_sales || 0, color: '#6366f1' },
          { label: "Today's Sales", value: stats.daily_sales || 0, color: '#06b6d4' },
          { label: 'Total Due', value: money(stats.total_due), color: '#dc2626' },
          { label: 'Total Paid Amount', value: money(stats.total_paid_amount), color: '#16a34a' },
          { label: 'Balance Amount To Pay', value: money(stats.balance_amount), color: '#b91c1c' },
          { label: 'Total Installation', value: money(stats.total_installation), color: '#f97316' },
          { label: 'Total Payment Done', value: money(stats.total_payment_done), color: '#14b8a6' },
          { label: 'Daily Payment', value: money(stats.daily_payment), color: '#8b5cf6' },
        ].map(card => (
          <div key={card.label} style={{ background: 'white', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderTop: `4px solid ${card.color}` }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>

      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#374151' }}>Date Filter:</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', color: '#6b7280' }}>From:</label>
          <input type="date" style={inp} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', color: '#6b7280' }}>To:</label>
          <input type="date" style={inp} value={toDate} onChange={e => setToDate(e.target.value)} />
        </div>
        <button onClick={applyFilter} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Apply Filter
        </button>
        <button onClick={clearFilter} style={{ background: '#6b7280', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          Clear
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { key: 'customers', label: 'Customer Full Data' },
          { key: 'sales', label: 'Sales' },
          { key: 'services', label: 'Services' },
          { key: 'payments', label: 'Payments' },
          { key: 'ledger', label: 'Credit/Debit Ledger' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: activeTab === tab.key ? '#1e3a5f' : 'white', color: activeTab === tab.key ? 'white' : '#374151', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', fontSize: '14px' }}>
            {tab.label}
          </button>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      {activeTab === 'customers' && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1040px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1e3a5f', color: 'white' }}>
                {['#', 'Name', 'Mobile', 'Address', 'Vehicle No', 'Device', 'IMEI', 'Installation Date', 'Renewal Date', 'Payment'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr><td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No customers found</td></tr>
              ) : customers.map((c, i) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{c.name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.mobile || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.address || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.vehicle_no || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.device_type || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.imei || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.installation_date ? formatDate(c.installation_date) : '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.renewal_date ? formatDate(c.renewal_date) : '-'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: c.payment_status === 'paid' ? '#d1fae5' : c.payment_status === 'pending' ? '#fee2e2' : '#fef3c7', color: c.payment_status === 'paid' ? '#065f46' : c.payment_status === 'pending' ? '#dc2626' : '#92400e' }}>
                      {c.payment_status || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'sales' && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '1240px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                {['#', 'Customer', 'Mobile', 'Vehicle', 'Product', 'IMEI', 'SIM No', 'Sale Total', 'Paid Amount', 'Balance', 'Installation', 'Payment', 'Date'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleSales.length === 0 ? (
                <tr><td colSpan="13" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No sales found</td></tr>
              ) : visibleSales.map((s, i) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{s.customer_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.customer_mobile || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.vehicle_no || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.product_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.imei || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.sim_no || s.serial_no || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#10b981' }}>{money(s.total_amount)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#2563eb' }}>{money(s.paid_amount)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: Number(s.balance_amount || 0) > 0 ? '#dc2626' : '#10b981' }}>{money(s.balance_amount)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: '#f97316', fontWeight: 'bold' }}>{money(s.installation_charge)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.payment_status || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{s.created_at ? formatDate(s.created_at) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'payments' && (
        <div>
          <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '16px', marginBottom: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {[
              { label: 'Total Credit', value: money(totalCredit), color: '#10b981' },
              { label: 'Cash', value: money(modeTotal(['cash'])), color: '#047857' },
              { label: 'Credit Mode', value: money(modeTotal(['credit'])), color: '#f97316' },
              { label: 'Online', value: money(modeTotal(['online', 'upi'])), color: '#3b82f6' },
              { label: 'GPay / Paytm / PhonePe', value: money(modeTotal(['gpay', 'paytm', 'phonepe'])), color: '#8b5cf6' },
            ].map((item) => (
              <div key={item.label}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6b7280' }}>{item.label}</p>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: item.color }}>{item.value}</p>
              </div>
            ))}
          </div>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['#', 'From', 'Mobile', 'Vehicle', 'Amount', 'Mode', 'Reference', 'Note', 'Recorded By', 'Date'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan="10" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No payments found</td></tr>
                ) : payments.map((p, i) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{p.from_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.from_mobile || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.vehicle_no || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#10b981' }}>{money(p.amount)}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{paymentLabels[p.payment_mode] || p.payment_mode || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.upi_ref || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.note || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.recorded_by_name || '-'}</td>
                    <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.created_at ? formatDate(p.created_at) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'services' && (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1040px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1e3a5f', color: 'white' }}>
                {['#', 'Customer', 'Mobile', 'Vehicle', 'Service', 'Start Date', 'Expiry Date', 'Status', 'Assigned By'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleServices.length === 0 ? (
                <tr><td colSpan="9" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No services found</td></tr>
              ) : visibleServices.map((service, i) => (
                <tr key={service.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{service.customer_name || service.linked_customer_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.customer_mobile || service.linked_customer_mobile || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.vehicle_no || service.linked_vehicle_no || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.service_name || '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.start_date ? formatDate(service.start_date) : '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.expiry_date ? formatDate(service.expiry_date) : '-'}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{String(service.status || '-').replace(/_/g, ' ')}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{service.created_by_name || service.assigned_user_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'ledger' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '14px' }}>
            {[
              { label: 'Final Debit', value: money(finalDebit), color: '#dc2626' },
              { label: 'Final Credit', value: money(finalCredit), color: '#059669' },
              { label: 'Final Balance', value: money(Math.abs(finalBalance)), color: finalBalance > 0 ? '#dc2626' : finalBalance < 0 ? '#059669' : '#0f172a' },
              { label: 'Final Status', value: finalStatus, color: finalBalance > 0 ? '#dc2626' : finalBalance < 0 ? '#059669' : '#0f172a' },
            ].map((item) => (
              <div key={item.label} style={{ background: 'white', borderRadius: '10px', padding: '14px', border: '1px solid #e5e7eb', boxShadow: '0 1px 5px rgba(15,23,42,0.06)' }}>
                <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#64748b', fontWeight: 800 }}>{item.label}</p>
                <strong style={{ color: item.color, fontSize: item.label === 'Final Status' ? '16px' : '20px' }}>{item.value}</strong>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button type="button" onClick={downloadDealerLedger} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '9px 15px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
              Download Dealer Ledger Details
            </button>
          </div>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '1480px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['#', 'Type', 'Transaction', 'Account', 'Vehicle', 'IMEI / Service', 'Debit', 'Credit', 'Balance', 'Cash', 'Credit Mode', 'Online', 'GPay', 'Paytm', 'PhonePe', 'Last Payment'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 ? (
                  <tr><td colSpan="16" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No ledger records found</td></tr>
                ) : ledger.map((l, i) => {
                  const debit = Number(l.total_debit ?? l.total_purchase ?? 0);
                  const credit = Number(l.total_credit ?? l.total_paid ?? 0);
                  const balance = Number(l.pending ?? (debit - credit));
                  const transaction = l.invoice_no || l.reference_no || l.transaction_id || '-';
                  const itemName = l.imei || l.service_name || l.device_name || '-';
                  return (
                    <tr key={l.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{l.row_type || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{transaction}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{l.account_name || l.dealer_name || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.vehicle_no || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{itemName}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(debit)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#10b981', fontWeight: 'bold' }}>{money(credit)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: balance > 0 ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>{money(balance)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.cash_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.credit_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.online_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.gpay_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.paytm_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.phonepe_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.last_payment_date ? formatDate(l.last_payment_date) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #1e3a5f' }}>
                  <td colSpan="6" style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 'bold' }}>Final Amount</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 'bold' }}>{money(finalDebit)}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 'bold', color: '#059669' }}>{money(finalCredit)}</td>
                  <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 'bold', color: finalBalance > 0 ? '#dc2626' : '#059669' }}>{money(Math.abs(finalBalance))} - {finalStatus}</td>
                  <td colSpan="7" />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
